import classNames from 'classnames';
import React, { memo } from 'react';
import { abbreviateNumber } from '../../utils/abbreviateNumber/abbreviateNumber';
import ButtonLink from '../ButtonLink/ButtonLink';
import { ButtonType } from '../../interfaces/enums/ButtonType.enum';
import { ButtonSize } from '../../interfaces/enums/ButtonSize.enum';
import {
  Accordion,
  AccordionItem,
  AccordionItemButton,
  AccordionItemHeading,
  AccordionItemPanel,
  AccordionItemState,
} from 'react-accessible-accordion';
import { v4 as uuidv4 } from 'uuid';
import { Table, TaxBand, TaxTable } from './StampDutyCalculator';
import { numberWithCommas } from '../../utils/numberWithCommas/numberWithCommas.util';
import { LinkProps } from '../../interfaces/LinkProps.interface';
import { roundNumber } from '../../utils/roundNumber/roundNumber.util';
import StoryblokLinkProvider from '../Utils/StoryblokLinkProvider/StoryblokLinkProvider';

interface StampDutyResultsProps {
  name: string;
  table: TaxTable;
  cta: LinkProps[];
}

export enum CountryType {
  WALES = 'wales',
  ENGLAND = 'england',
  SCOTLAND = 'scotland',
}

const StampDutyResults: React.FC<StampDutyResultsProps> = ({
  name,
  table,
  cta,
}) => {
  const { taxBands, footnote, buyingOption, propertyPrice } = table;

  const tableClasses = classNames(
    'table-fixed w-full text-left  divide-[#C8CECC] border-t border-x border-[#C8CECC] text-left rounded-t-[8px] border-separate min-w-[600px]'
  );
  const rowClasses = classNames('divide-[#C8CECC]');
  const cellClasses = 'px-3 py-4 text-sm sm:text-base';
  const headerClasses = 'bg-[#E2EDF8]';
  const bodyClasses = 'bg-[#ffffff]';

  const taxResult = table.taxBands?.reduce((acc, band) => acc + band.tax, 0);

  return (
    <>
      <div className="bg-[#ECFDF3] text-center rounded-[12px] border border-[#039855] p-10 space-y-6">
        <div className="space-y-3">
          {name && (
            <p className="text-[#027A48] text-xl font-dm-serif-display">
              {name} on your property is:
            </p>
          )}
          <p className="text-[#027A48] text-[32px] font-bold">
            £{taxResult ? `${numberWithCommas(roundNumber(taxResult))}` : '0'}
          </p>
          <p className="text-[#027A48]">
            {buyingOption} - £{numberWithCommas(propertyPrice)}
          </p>
        </div>
        <div className="text-center">
          {cta.map((ctaItem: LinkProps) => (
            <StoryblokLinkProvider
              key={ctaItem._uid}
              link={ctaItem.link}
              render={(link) => (
                <div className="hidden sm:block" key={uuidv4()}>
                  <ButtonLink
                    url={link}
                    label={ctaItem.label}
                    type={ButtonType.PRIMARY}
                    size={ButtonSize.XL}
                    className="uppercase"
                  >
                    {ctaItem.label}
                  </ButtonLink>
                </div>
              )}
            />
          ))}
        </div>
      </div>
      {taxBands && taxBands?.length && (
        <Accordion allowZeroExpanded>
          <AccordionItem
            key={'tax_table'}
            uuid={'tax_table'}
            className="group w-full"
          >
            <AccordionItemHeading>
              <AccordionItemButton className="focus:ring-0 focus:outline-none">
                <AccordionItemState>
                  {(state) =>
                    state && (
                      <div className="p-4 bg-system/white border border-border/default rounded-[8px] flex items-center justify-between mt-10 cursor-pointer">
                        <div className="flex items-center justify-start space-x-2">
                          <span
                            style={{
                              backgroundImage: "url('/svgs/calculator.svg')",
                            }}
                            className="h-[32px] w-[32px] bg-no-repeat transition-all bg-cover block"
                          ></span>
                          <p className="font-bold">Full tax calculation</p>
                        </div>
                        <span
                          className={`h-[24px] w-[24px] bg-no-repeat transition-all ease-in-out bg-button/primary-bg ${
                            state.expanded ? 'rotate-180' : ''
                          }`}
                          style={{
                            WebkitMaskImage: "url('/svgs/chevron.svg')",
                            maskImage: "url('/svgs/chevron.svg')",
                          }}
                        />
                      </div>
                    )
                  }
                </AccordionItemState>
              </AccordionItemButton>
            </AccordionItemHeading>

            <AccordionItemPanel className="pt-4 overflow-x-scroll sm:overflow-x-visible">
              <table className={tableClasses}>
                <tbody>
                  <tr className={rowClasses}>
                    <th className={classNames(cellClasses, headerClasses)}>
                      Tax Band
                    </th>
                    <th className={classNames(cellClasses, headerClasses)}>
                      %
                    </th>
                    <th className={classNames(cellClasses, headerClasses)}>
                      Taxable Sum
                    </th>
                    <th className={classNames(cellClasses, headerClasses)}>
                      Tax
                    </th>
                  </tr>
                  {taxBands.map((band, idx) => {
                    return (
                      <tr className={rowClasses} key={uuidv4()}>
                        <td
                          className={classNames(
                            cellClasses,
                            bodyClasses,
                            'font-bold'
                          )}
                        >
                          {band.taxBand}
                        </td>
                        <td className={classNames(cellClasses, bodyClasses)}>
                          {band.rate}
                        </td>
                        <td className={classNames(cellClasses, bodyClasses)}>
                          £{numberWithCommas(roundNumber(band.taxableSum))}
                        </td>
                        <td className={classNames(cellClasses, bodyClasses)}>
                          £{numberWithCommas(roundNumber(band.tax))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div
                className={classNames(
                  cellClasses,
                  headerClasses,
                  'table-auto border-b border-r border-l border-[#C8CECC] text-left rounded-b-[8px] text-sm w-full min-w-[600px]'
                )}
              >
                {footnote}
              </div>
            </AccordionItemPanel>
          </AccordionItem>
        </Accordion>
      )}
    </>
  );
};

export default memo(StampDutyResults);
